import React, { useState } from "react";
import { CrCheckbox, DashedCheckboxBox, ShareBadge, PrimaryButton } from "./v2";

// The club radar's "pick a list / + New list" rows — lifted OUT of
// ClubSaveToRadarPicker (frames 1l/1m) so a second surface can use the exact same
// affordance instead of growing its own. Callers so far:
//   · profile/ClubSaveToRadarPicker — the Add-to-Radar popover/sheet (multi-select)
//   · scout-request/CloseScoutRequestDialog — the close dialog's keep branch, which
//     picks ONE target list (DECISION-002)
// Presentation only: the caller owns the lists, the checked state and the create call
// (always clubRadarService.createList — there is no second way to make a list).
//
// States per DESIGN-SPEC `pickLists`: already-in (green pre-checked look, NOT
// toggleable, "Already in this list" caption) · selected (blue checkbox) · unselected.

// List-count caption (right side of a row) — rendered only when the payload carries
// a count; real values, never invented.
export const listCount = (list) => {
  const n = list.entry_count ?? list.player_count ?? list.count;
  return n == null ? null : String(n);
};

export const ListRow = ({ list, checked, onToggle, mobile, role = "checkbox" }) => {
  const state = list.already_in ? "already" : checked ? "checked" : "unchecked";
  const count = listCount(list);
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        if (!list.already_in) onToggle(list.id);
      }}
      role={role}
      aria-checked={checked || !!list.already_in}
      aria-disabled={list.already_in || undefined}
      tabIndex={0}
      style={{
        display: "flex",
        alignItems: "center",
        gap: mobile ? 11 : 10,
        padding: mobile ? "11px 4px" : "9px 8px",
        borderRadius: mobile ? 0 : 10,
        borderBottom: mobile ? "1px solid #F8FAFC" : "none",
        cursor: list.already_in ? "default" : "pointer",
        opacity: list.already_in ? 0.85 : 1,
      }}
    >
      <CrCheckbox
        state={state}
        size={mobile ? 20 : 18}
        radius={mobile ? 7 : 6}
        tickSize={mobile ? 11 : 10}
      />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span
          style={{
            fontSize: mobile ? 14 : 13.5,
            fontWeight: 600,
            color: list.already_in ? "#6B7280" : "#111827",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {list.name}
        </span>
        {list.share_tier && list.share_tier !== "personal" && (
          <ShareBadge pad="1px 7px" fontSize={9.5}>Shared</ShareBadge>
        )}
      </div>
      {list.already_in ? (
        <span style={{ fontSize: 11.5, fontWeight: 600, color: "#047857", whiteSpace: "nowrap", flex: "none" }}>
          Already in this list
        </span>
      ) : count != null ? (
        <span style={{ fontSize: 11.5, fontWeight: 600, color: "#9CA3AF", whiteSpace: "nowrap", flex: "none" }}>
          {count}
        </span>
      ) : null}
    </div>
  );
};

export const NewListRow = ({ mobile, open, setOpen, name, setName, creating, onSubmitNew }) =>
  open ? (
    <div style={{ display: "flex", gap: 6, padding: mobile ? "7px 4px" : "6px 8px" }}>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSubmitNew()}
        autoFocus
        placeholder="List name"
        maxLength={60}
        className="flex-1 min-w-0 outline-none"
        style={{
          height: mobile ? 44 : 32,
          boxSizing: "border-box",
          border: "1px solid #E5E7EB",
          borderRadius: 10,
          padding: "0 10px",
          fontSize: 12.5,
          fontFamily: "inherit",
        }}
      />
      <PrimaryButton
        height={mobile ? 44 : 32}
        radius={10}
        fontSize={12.5}
        pad={mobile ? "0 15px" : "0 12px"}
        disabled={creating}
        onClick={onSubmitNew}
      >
        Add
      </PrimaryButton>
    </div>
  ) : (
    <div
      onClick={() => setOpen(true)}
      role="button"
      tabIndex={0}
      style={{
        display: "flex",
        alignItems: "center",
        gap: mobile ? 11 : 10,
        padding: mobile ? "11px 4px" : "9px 8px",
        borderRadius: mobile ? 0 : 10,
        cursor: "pointer",
      }}
    >
      <DashedCheckboxBox size={mobile ? 20 : 18} radius={mobile ? 7 : 6} fontSize={mobile ? 13 : 12} />
      <span style={{ fontSize: mobile ? 14 : 13.5, fontWeight: 700, color: "#2283FF" }}>New list</span>
    </div>
  );

// The "+ New list" row wired to its own open/name state + the caller's create call —
// both callers repeat exactly this, so it lives here once.
export const InlineNewList = ({ mobile, creating, onCreateList }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const submitNew = async () => {
    const v = name.trim();
    if (!v) {
      setOpen(false);
      return;
    }
    const ok = await onCreateList(v);
    if (ok) {
      setName("");
      setOpen(false);
    }
  };
  return (
    <NewListRow
      mobile={mobile}
      open={open}
      setOpen={setOpen}
      name={name}
      setName={setName}
      creating={creating}
      onSubmitNew={submitNew}
    />
  );
};
